import { CommonModule } from '@angular/common';
import { Component, computed, effect, input, OnInit, signal } from '@angular/core';
import { ActivatedRoute, UrlSegment } from '@angular/router';

import { first } from 'rxjs';

import { CommunitiesSupported, SpecializedSupport, SpecializedSupportDisplay } from '@sondermind/constants';
import { ClientTestimonialHttpService } from '@sondermind/data-access/testimonials';
import { Logger } from '@sondermind/datadog-logging';
import { PillComponent } from '@sondermind/features/pill';
import { generateExperienceSupportingList } from '@sondermind/helpers/provider';
import { IrisIconComponent, IconSizes, IconUsageTypes } from '@sondermind/iris/icon';
import {
  ICertification,
  IProviderNetworkCompany,
  IProviderShareableProfile
} from '@sondermind/utilities/models-public-facing';
import { USStateAbbrevMap } from '@sondermind/utilities/states-list';

import { ITopSpecialty } from '../../models/top-specialty.interface';

enum HeaderNames {
  ABOUT = 'About',
  SPECIALIZES_IN = 'Specializes in',
  WORK_WITH = 'Works with',
  INSURANCE_AND_LICENSURE = 'Insurance & Licensure',
}

enum SexualitiesSupported {
  QUEER = 'Queer',
  LGBTQIA = 'LGBTQIA+',
}

const headerNameToHTMLSectionNameMap = new Map<string, string>([
  [HeaderNames.ABOUT, 'about'],
  [HeaderNames.SPECIALIZES_IN, 'specialties'],
  [HeaderNames.WORK_WITH, 'works-with'],
  [HeaderNames.INSURANCE_AND_LICENSURE, 'insurance-and-licensure'],
]);

/**
 * This component displays tabbed, linked sections of a provider's profile data
 * To add a new section, make sure to do all of the following:
 * 1. Add the title of the section to moduleNames below
 * 2. Add the HTML of the section in the .html file
 * 3. Add an id to the <section> tag that matches the first word of the title in moduleNames + -section
 * 4. Create a link <a> tag to the section around the header, with the href being
 *   the currentRoute + a fragment of the same first word
 * 5. Add an id to that tag that matches the first word of the title in moduleNames + -link
 * This ensures the section is reachable from the header and is accessible by maintaining tab focus
 */
@Component({
  selector: 'provider-details',
  imports: [
    CommonModule,
    IrisIconComponent,
    PillComponent,
  ],
  templateUrl: './provider-details.component.html',
  styleUrls: ['./provider-details.component.scss']
})
export class ProviderDetailsComponent implements OnInit {
  readonly providerShareableProfile = input.required<IProviderShareableProfile>();
  readonly topSpecialties = input<ITopSpecialty[]>([]);

  isValid: boolean = false;
  experienceSupportingList: string[] = [];
  providerBio: string = '';
  allSortedInsurances: string[] = [];
  allOtherSortedSpecialties: string[] = [];

  providerNetworkCompaniesGroupedByInsuranceName: IProviderNetworkCompany[][] = [];

  licensures: string[] = [];
  stateLicenses: string[] = [];

  headerNames = HeaderNames;

  specialtiesModuleName: string = 'specialties';
  lastSelectedSection: string = '';
  fragment: string = '';
  currentRoute: string = '';

  iconSizes = IconSizes;
  iconUsageTypes = IconUsageTypes;
  agesSupported: string[] = [];
  readonly communitiesSupported = signal<string[]>([]);
  treatmentApproaches: string[] = [];

  showTestimonial: boolean = false;
  latestTestimonialFeedback: string = '';
  latestTestimonialSource: string = '';

  readonly showBioSection = computed(() => {
    const profile = this.providerShareableProfile();
    return profile.bio?.length;
  });

  readonly showSpecialtiesSection = computed(() => {
    const profile = this.providerShareableProfile();
    return profile.specialties?.length;
  });

  readonly showWorksWithSection = computed(() => {
    const profile = this.providerShareableProfile();
    const communities = this.communitiesSupported();
    return profile.ages.length
    || profile.languages.length
    || profile.genders_served.length
    || communities.length
    || profile.treatment_approaches.length;
  });

  readonly showInsuranceLicensuresSection = computed(() => {
    const profile = this.providerShareableProfile();
    return profile.certifications?.length
    || profile.provider_network_companies?.length;
  });

  moduleNames: string[] = [];

  readonly setModuleNames = effect(() => {
    const names: HeaderNames[] = [];
    if (this.showBioSection()) names.push(HeaderNames.ABOUT);
    if (this.showSpecialtiesSection()) names.push(HeaderNames.SPECIALIZES_IN);
    if (this.showWorksWithSection()) names.push(HeaderNames.WORK_WITH);
    if (this.showInsuranceLicensuresSection()) names.push(HeaderNames.INSURANCE_AND_LICENSURE);
    this.moduleNames = names;
    // eslint-disable-next-line prefer-destructuring
    this.lastSelectedSection = this.moduleNames.length ? this.moduleNames[0] : '';
  });

  constructor(
    private route: ActivatedRoute,
    private logger: Logger,
    private clientTestimonialHttpService: ClientTestimonialHttpService
  ) {}

  ngOnInit(): void {
    this.providerBio = this.providerShareableProfile().bio;

    this.experienceSupportingList = generateExperienceSupportingList(
      this.providerShareableProfile().ages.map(({ name }) => name),
      this.providerShareableProfile().services_offered.map(({ name }) => name),
      this.providerShareableProfile().specialties.map(({ name }) => name)
    );

    this.route.snapshot.url.forEach((urlsegment: UrlSegment) => {
      this.currentRoute += `/${urlsegment.path.toString()}`;
    });
    this.route.fragment.subscribe((fragment) => { this.fragment = fragment ?? '' });

    this.sortProviderData();
    this.arrangeSpecialties();
    this.generateAgesList();
    this.generateCommunitiesSupportedList();
    this.generateTreatmentApproachList();
    this.prepareLicenseDisplayData();
    this.prepareTestimonialDisplayData();
  }

  ngAfterViewInit(): void {
    try {
      if (this.fragment) {
        const element = document.getElementById(`${this.fragment}-section`);
        if (element) {
          element.scrollIntoView();
        }
      }
    } catch (e) {
      this.logger.error(e instanceof Error ? e.message : String(e));
    }
  }

  private arrangeSpecialties(): void {
    // Remove the top specialties from the list, since they are shown only in the pills
    if (this.topSpecialties().length > 0) {
      this.allOtherSortedSpecialties = this.allOtherSortedSpecialties
        .filter((specialityName) => !this.topSpecialties()
          .find((topSpecialty) => topSpecialty.name === specialityName)
        );
    }
  }

  /**
   * Since we are taking in the header strings, they may have a space.
   * This function specifically trims the name down to the first word before a space, as the
   * element id will not have a space in it
   */
  scrollToSection(sectionName: string): void {
    // Section names should not have extra white space, but trim just in case
    const trimmedSectionName = sectionName.trim();
    this.lastSelectedSection = trimmedSectionName;

    const firstWordOfElementId = headerNameToHTMLSectionNameMap.get(trimmedSectionName);
    if (firstWordOfElementId) {
      const element = document.getElementById(`${firstWordOfElementId.toLocaleLowerCase()}-section`);
      const linkToFocus = document.getElementById(`${firstWordOfElementId.toLocaleLowerCase()}-link`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      if (linkToFocus) {
        linkToFocus.focus({ preventScroll: true });
      }
    }
  }

  private generateAgesList(): void {
    const ageDisplayMapping = new Map<string, string>([
      [SpecializedSupport.INFANTS, SpecializedSupportDisplay.CHILDREN],
      [SpecializedSupport.CHILDREN, SpecializedSupportDisplay.CHILDREN],
      [SpecializedSupport.ADOLESCENTS, SpecializedSupportDisplay.ADOLESCENTS],
      [SpecializedSupport.ADULTS, SpecializedSupportDisplay.ADULTS],
      [SpecializedSupport.SENIORS, SpecializedSupportDisplay.SENIORS],
    ]);

    const ageNames = this.providerShareableProfile().ages.map(({ name }) => name);
    const uniqueDisplays = new Set<string>();

    ageDisplayMapping.forEach((displayName, range) => {
      if (ageNames.includes(range)) {
        uniqueDisplays.add(displayName);
      }
    });

    this.agesSupported = [...uniqueDisplays];
  }

  private generateCommunitiesSupportedList(): void {
    const communitiesSupported: Array<{ name: string; }> = this.providerShareableProfile().specialties
      .filter((specialty) => specialty.name === CommunitiesSupported.LGBTQIA ||
        specialty.name === CommunitiesSupported.VETERAN ||
        specialty.name === CommunitiesSupported.PERINATAL
      );

    if (Array.isArray(this.providerShareableProfile().religions)) {
      communitiesSupported.push(...this.providerShareableProfile().religions);
    }

    if (Array.isArray(this.providerShareableProfile().sexualities)) {
      // Use the 'LGBTQIA+' label if the provider's supported sexualties includes the umbrella term 'Queer',
      // or if there are at least 3 other supported sexualties
      if (this.providerShareableProfile().sexualities.find(({ name }) => name === SexualitiesSupported.QUEER)
        || this.providerShareableProfile().sexualities.length >= 3
      ) {
        communitiesSupported.push({ name: SexualitiesSupported.LGBTQIA });
      } else {
        communitiesSupported.push(...this.providerShareableProfile().sexualities);
      }
    }
    this.communitiesSupported.set(communitiesSupported
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((community) => community.name));
  }

  private generateTreatmentApproachList(): void {
    this.treatmentApproaches = this.providerShareableProfile().treatment_approaches.map(({ name }) => name);
  }

  // prepares license info for display
  private prepareLicenseDisplayData(): void {
    // ex. Licensed Marriage and Family Therapist (LMFT)
    const formattedLicensures: string[] = [];

    // ex. Colorado: 12345678
    const formattedStateLicenses: string[] = [];

    this.providerShareableProfile().certifications
      .filter((cert: ICertification) => !!cert.issue_state && !!cert.cert_number)
      .forEach((cert) => {
        // format licensures
        const formattedLicensure = cert.abbrev
          ? `${cert.name} (${cert.abbrev})`
          : cert.name;

        formattedLicensures.push(formattedLicensure);

        // format state licensures
        const stateName = USStateAbbrevMap[cert.issue_state] ?? cert.issue_state;
        const formattedStateLicense = `${stateName}: ${cert.cert_number}`;
        formattedStateLicenses.push(formattedStateLicense);
      });

    this.licensures = formattedLicensures;
    this.stateLicenses = formattedStateLicenses;
  }

  private prepareTestimonialDisplayData(): void {
    this.clientTestimonialHttpService.getTestimonialsForProvider(this.providerShareableProfile()?.id).pipe(
      first()
    ).subscribe(
      (data) => {
        const LATEST_TESTIMONIAL = data?.find(
          (testimonial) => !!testimonial.clientState && !!testimonial.dateFeedbackCollected && !!testimonial.feedback
        );

        if (LATEST_TESTIMONIAL) {
          this.showTestimonial = true;

          this.latestTestimonialFeedback = LATEST_TESTIMONIAL.feedback;

          const STATE_LONG_NAME = USStateAbbrevMap[LATEST_TESTIMONIAL.clientState] ?? LATEST_TESTIMONIAL.clientState;
          this.latestTestimonialSource = ` – ${STATE_LONG_NAME} • ${LATEST_TESTIMONIAL.dateFeedbackCollected}`;
        }
      }
    );
  }

  private sortProviderData(): void {
    this.allOtherSortedSpecialties = this.providerShareableProfile().specialties
      .map((specialty) => specialty.name)
      .sort((s1, s2) => s1.localeCompare(s2));

    this.sortInsurances();
  }

  private sortInsurances(): void {
    const providerNetworkCompanies = this.providerShareableProfile().provider_network_companies;
    const pncsByInsuranceGroupMap = new Map<number, IProviderNetworkCompany[]>();

    // Group pncs by the insurance group they belong to
    // All pncs in the same insurance group have the same insurance_id
    providerNetworkCompanies.forEach((pnc) => {
      if (pnc.insurance_name && pnc.insurance_id) {
        if (pncsByInsuranceGroupMap.has(pnc.insurance_id)) {
        // Check for uniqueness by name since there can be multiple insurances of the same name
        // in different network states
          const pncs = pncsByInsuranceGroupMap.get(pnc.insurance_id);
          if (pncs && !pncs.find((p) => p.name === pnc.name)) {
            pncs.push(pnc);
          }
        } else {
          pncsByInsuranceGroupMap.set(pnc.insurance_id, [pnc]);
        }
      }
    });

    // Sort the individual lists of pncs alphabetically
    pncsByInsuranceGroupMap.forEach((value) => {
      if (value.length > 0) {
        const sortedPncs = value.sort((pnc1, pnc2) => pnc1?.name.localeCompare(pnc2?.name));
        this.providerNetworkCompaniesGroupedByInsuranceName.push(sortedPncs);
      }
    });

    // Sort the insurance group names alphabetically
    this.providerNetworkCompaniesGroupedByInsuranceName.sort(
      (pncList1, pncList2) => pncList1[0].insurance_name.localeCompare(pncList2[0].insurance_name)
    );
  }
}
